/* «Повтори движения» / «Стать персонажем» на движке шаблона: состояние экрана S (features/dance/engine.ts) ↔ входы формы сервера
   (план движений §2.1 push/pull). Вид (DanceView) — то, что экран говорит заказу словами модели: кто повторит, ролик и фрагмент,
   разовое фото и его кадр, фон, голос.
   - viewFromEngine(h, uploads) — вид из S и крючков движка (зеркало S → входы в syncFoot);
   - inputsOf(form, view, prev) — входы по виду; пишет только опубликованные ключи (родные правила MotionOrderForm, MotionHero, MotionClip,
     MotionSettings): кто повторит — cast или разовое фото (одно из двух), фрагмент start_ms/end_ms, кадр фото crop_x/crop_y/zoom, bg,
     голос (voice или voice_mode) и voice_id; id загрузок пишет мост загрузок — здесь фото только снимается, если повторит не человек с фото;
     согласие (consent_accepted) пишет только лист согласия;
   - viewOf(form, inputs) — обратно: только то, что входы определяют (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы);
   - applyView(h, view) — вид в S (кто повторит, фон, голос); файлы — у сервера экрана.
   Пределы фрагмента — из политики формы (Q-M1, план вехи 3 §6: duration_limits как у шаблона, без родного предела 15 с).
   Чистые inputsOf и viewOf проходят круг: viewOf(form, inputsOf(form, view, {})) ⊆ view (dance-inputs.test.ts). */
import { record } from '../studio/studio-content';
import type { StudioForm } from '../studio/studio-api';
import { toolField } from '../studio/circle/tool-form';
import { selfRef, type SelfRef } from '../studio/engine-kit/people-bridge';
import { fill, motionPolicy, motionVoiceKey, motionVoiceModes, motionWords, type MotionPreset, type MotionVoiceMode } from '../motion/motion-model';

type Inputs = Record<string, string>;
/** Кто повторит: «Вы» (свои фото или вариант), близкий, персонаж, человек с фото */
export type DanceWho = 'me' | 'p' | 'char' | 'ph';
export interface DanceCrop {
  cx: number;
  cy: number;
  z: number;
}
export interface DanceView {
  who: DanceWho | null;
  /** «Вы»: свои фото или вариант */
  self: SelfRef | null;
  /** Близкий (who p) */
  person: number | null;
  /** Персонаж (who char) */
  char: number | null;
  /** Вид персонажа (skin_id карточки каталога): в строке состава character:<id>:skin:<id> — картинку вида отдаёт Voice, как у кружка */
  skin?: number | null;
  /** Фрагмент ролика, мс; нет ролика или длины — null */
  trim: { start: number; end: number } | null;
  /** Кадр разового фото */
  crop: DanceCrop | null;
  bg: 'video' | 'hero';
  vm: MotionVoiceMode;
  /** Голос каталога (vm voice) */
  voice: number | null;
  /** id готовых загрузок файлов, которые держит сервер экрана: ролик, разовое фото */
  uploads?: Partial<Record<'video' | 'photo', string>>;
}

const CROP_KEYS = ['crop_x', 'crop_y', 'zoom'] as const;
const TRIM_KEYS = ['start_ms', 'end_ms'] as const;
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const has = (form: StudioForm, key: string) => !!toolField(form, key);
const published = (form: StudioForm, key: string, value: string) => {
  const field = toolField(form, key);
  return !!field && (!field.values.length || field.values.includes(value));
};

/** Пресет экрана: политика формы, иначе вход приложения (swap — «Стать персонажем») */
export function dancePreset(form: StudioForm | null | undefined, entry?: Record<string, unknown> | null): MotionPreset {
  const policy = form ? motionPolicy(form) : null;
  if (policy) return policy.preset;
  return entry?.preset === 'swap' || entry?.screen === 'swap' ? 'swap' : 'dance';
}
/** Пределы фрагмента по фону, мс: duration_limits политики (как шаблон: от 3 до 30 с), без своего предела клиента (Q-M1) */
export function danceLimits(form: StudioForm | null | undefined, bg: string | undefined): { minMs: number; maxMs: number } {
  const policy = form ? motionPolicy(form) : null;
  const limit = record(record(policy?.duration_limits)[bg === 'hero' ? 'hero' : 'video']);
  const min = Number(limit.min_ms),
    max = Number(limit.max_ms);
  const minMs = Number.isFinite(min) && min > 0 ? min : 3000;
  const maxMs = Number.isFinite(max) && max >= minMs ? max : Math.max(minMs, 30000);
  return { minMs, maxMs };
}
/** Фоны, которые форма публикует (значения поля bg ∩ policy.backgrounds) */
export function danceBackgrounds(form: StudioForm | null | undefined): Array<'video' | 'hero'> {
  if (!form) return [];
  const policy = motionPolicy(form);
  const allowed = Array.isArray(policy?.backgrounds) ? (policy!.backgrounds as unknown[]).map(String) : ['video', 'hero'];
  const field = toolField(form, 'bg');
  return (field?.values ?? []).filter((v): v is 'video' | 'hero' => (v === 'video' || v === 'hero') && allowed.includes(v));
}

/** Входы формы по виду (родные правила, только опубликованные ключи). prev — нынешние входы: id загрузок и неизвестные виду ключи остаются */
export function inputsOf(form: StudioForm | null | undefined, view: DanceView, prev: Inputs): Inputs {
  if (!form) return prev;
  const next: Inputs = { ...prev };
  const put = (key: string, value: string | null | undefined) => {
    if (!has(form, key)) return;
    if (value === null || value === undefined || value === '') delete next[key];
    else next[key] = value;
  };
  const who = view.who;
  /* кто повторит: состав или разовое фото — одно из двух (MotionHero) */
  let cast: string | null = null;
  if (who === 'me' && view.self) cast = view.self.src === 'avatar' ? `user_avatar:${view.self.avatar_id}` : 'self';
  else if (who === 'p' && view.person) cast = `person:${view.person}`;
  else if (who === 'char' && view.char) cast = `character:${view.char}${view.skin ? `:skin:${view.skin}` : ''}`;
  put('cast', cast);
  const ids = view.uploads ?? {};
  put('photo', who === 'ph' ? (ids.photo ?? prev.photo ?? null) : null);
  /* ролик пишет мост: здесь он только остаётся (убрать ролик — тоже мост) */
  if (ids.video) put('video', ids.video);
  /* кадр разового фото (MotionOrderForm finishCrop): только все три ключа, иначе ни одного */
  const cropOn = who === 'ph' && !!view.crop && CROP_KEYS.every(key => has(form, key));
  for (const key of CROP_KEYS) if (!cropOn) delete next[key];
  if (cropOn) {
    next.crop_x = view.crop!.cx.toFixed(4);
    next.crop_y = view.crop!.cy.toFixed(4);
    next.zoom = view.crop!.z.toFixed(2);
  }
  /* фрагмент ролика: оба ключа */
  if (TRIM_KEYS.every(key => has(form, key))) {
    if (view.trim) {
      next.start_ms = String(Math.max(0, Math.round(view.trim.start)));
      next.end_ms = String(Math.max(0, Math.round(view.trim.end)));
    } else for (const key of TRIM_KEYS) delete next[key];
  }
  /* фон и голос — только опубликованные значения */
  if (published(form, 'bg', view.bg) && danceBackgrounds(form).includes(view.bg)) next.bg = view.bg;
  const voiceKey = motionVoiceKey(form);
  if (toolField(form, voiceKey)) {
    const modes = motionVoiceModes(form);
    const vm = view.vm === 'src' || modes.includes(view.vm) ? view.vm : 'src';
    const mode = vm === 'voice' && !view.voice ? 'src' : vm;
    if (published(form, voiceKey, mode)) next[voiceKey] = mode;
    else delete next[voiceKey];
    put('voice_id', mode === 'voice' ? String(view.voice) : null);
  }
  /* пресет: только опубликованное значение пресета политики */
  const preset = motionPolicy(form)?.preset;
  if (preset && published(form, 'preset', preset)) next.preset = preset;
  return next;
}

/** «Вы», близкий или персонаж из строки состава */
function castOf(cast: string): Pick<DanceView, 'who' | 'self' | 'person' | 'char' | 'skin'> | null {
  if (cast === 'self') return { who: 'me', self: { src: 'photos' }, person: null, char: null };
  const [kind, raw, tag, skinRaw] = cast.split(':');
  const id = num(raw);
  if (!id) return null;
  if (kind === 'character' && tag === 'skin') return { who: 'char', self: null, person: null, char: id, skin: num(skinRaw) };
  if (kind === 'user_avatar') return { who: 'me', self: { src: 'avatar', avatar_id: id }, person: null, char: null };
  if (kind === 'person') return { who: 'p', self: null, person: id, char: null };
  if (kind === 'character') return { who: 'char', self: null, person: null, char: id };
  return null;
}

/** Вид по входам: только то, что входы определяют (нет кого повторит — нет и who; нет поля — нет значения) */
export function viewOf(form: StudioForm | null | undefined, inputs: Inputs): Partial<DanceView> {
  if (!form) return {};
  const out: Partial<DanceView> = {};
  if (inputs.photo) Object.assign(out, { who: 'ph', self: null, person: null, char: null });
  else if (inputs.cast) Object.assign(out, castOf(inputs.cast) ?? {});
  const start = Number(inputs.start_ms),
    end = Number(inputs.end_ms);
  if (inputs.end_ms && Number.isFinite(start) && Number.isFinite(end) && end > start && start >= 0) out.trim = { start, end };
  if (CROP_KEYS.every(key => inputs[key])) {
    const cx = Number(inputs.crop_x),
      cy = Number(inputs.crop_y),
      z = Number(inputs.zoom);
    if ([cx, cy, z].every(Number.isFinite)) out.crop = { cx, cy, z };
  }
  if (inputs.bg === 'video' || inputs.bg === 'hero') out.bg = inputs.bg;
  const mode = inputs[motionVoiceKey(form)];
  if (mode === 'src' || mode === 'hero') out.vm = mode;
  if (mode === 'voice') {
    const id = num(inputs.voice_id);
    out.vm = id ? 'voice' : 'src';
    out.voice = id;
  }
  return out;
}

/** Чего не хватает по входам (родной motionMissing, но пределы — duration_limits политики, Q-M1). Права и согласие проверяет «Создать»
    (лист согласия перед созданием), поэтому здесь их нет. hero — сведения ряда лиц (имя, нет фото); null — можно создавать */
export function danceMissing(
  form: StudioForm,
  inputs: Inputs,
  ctx: { hero?: { name: string; needsPhoto?: boolean } | null; cropOpen?: boolean } = {},
): string | null {
  const policy = motionPolicy(form);
  if (!policy || !form.available) return 'Создание сейчас недоступно';
  const words = motionWords(policy.preset);
  if (ctx.cropOpen) return 'Закончите кадр фото';
  if (!inputs.video) return words.upload;
  if (inputs.cast && inputs.photo) return 'Выберите одного героя';
  if (!inputs.cast && !inputs.photo) return words.who;
  if (ctx.hero?.needsPhoto) return fill('Добавьте фото: {0}', ctx.hero.name);
  const { minMs, maxMs } = danceLimits(form, inputs.bg);
  const start = Number(inputs.start_ms ?? 0),
    end = Number(inputs.end_ms);
  const len = end - start;
  if (!inputs.end_ms || !Number.isFinite(start) || !Number.isFinite(end) || start < 0 || len < minMs || len > maxMs)
    return fill('Выберите фрагмент от {0} до {1} секунд', minMs / 1000, maxMs / 1000);
  const voiceKey = motionVoiceKey(form),
    voice = inputs[voiceKey];
  if (voice && voice !== 'src' && !motionVoiceModes(form).includes(voice as MotionVoiceMode)) return 'Смена голоса пока недоступна';
  if (voice === 'voice' && !inputs.voice_id) return 'Выберите голос';
  if (inputs.bg && !['video', 'hero'].includes(inputs.bg)) return 'Выберите фон';
  return form.fields.some(f => f.required && !['cast', 'photo', 'consent_accepted'].includes(f.key) && !inputs[f.key]?.trim())
    ? 'Заполните обязательные поля'
    : null;
}

/* ---------- движок ---------- */

interface EngineClip {
  dur?: number;
  trim?: { from: number; to: number };
}
/** Крючки движка, которые читает и пишет вид (часть serve({...}) из engine.ts) */
export interface DanceViewHooks {
  S: {
    pre: string;
    ppl: Array<{ id: number | string; act: unknown; own: string }>;
    ph: { crop?: DanceCrop | null } | null;
    phOn: boolean;
    cid: unknown;
    clip: EngineClip | null;
    bg: string;
    vm: string;
    voice: unknown;
    auto: boolean;
    [key: string]: unknown;
  };
  readonly P: { setIn(on: boolean): void; inFrame(): boolean };
  who(): DanceWho | null;
  vmEff(): string;
  limits(): { min: number; max: number };
}
/** Вид персонажа в S (S.skin = '<персонаж>:<вид>'): действует, только пока выбран тот же персонаж — смена персонажа вид сбрасывает */
export function skinOf(value: unknown, char: number | null): number | null {
  if (!char || typeof value !== 'string') return null;
  const [owner, skin] = value.split(':');
  return num(owner) === char ? num(skin) : null;
}
/** Вид из S: кто повторит, фрагмент (BotCore.trim.of: у короткого — весь ролик, у длинного — первые max секунд), кадр фото, фон, голос на деле */
export function viewFromEngine(h: DanceViewHooks, uploads: DanceView['uploads'] = {}): DanceView {
  const { S } = h;
  const who = h.who();
  const clip = S.clip;
  let trim: DanceView['trim'] = null;
  const dur = clip?.dur ?? 0;
  if (clip && dur > 0) {
    const max = h.limits().max;
    const t = clip.trim && clip.trim.from >= 0 && clip.trim.to <= dur && clip.trim.to > clip.trim.from ? clip.trim : { from: 0, to: Math.min(dur, max) };
    trim = { start: Math.round(t.from * 1000), end: Math.round(t.to * 1000) };
  }
  const vm = h.vmEff();
  return {
    who,
    self: who === 'me' ? selfRef() : null,
    person: who === 'p' ? num(S.ppl[0]?.id) : null,
    char: who === 'char' ? num(S.cid) : null,
    skin: who === 'char' ? skinOf(S.skin, num(S.cid)) : null,
    trim,
    crop: who === 'ph' && S.ph?.crop ? { ...S.ph.crop } : null,
    bg: S.bg === 'hero' ? 'hero' : 'video',
    vm: vm === 'hero' || vm === 'voice' ? vm : 'src',
    voice: vm === 'voice' ? num(S.voice) : null,
    uploads,
  };
}

/** Вид в S: кто повторит (персонаж, близкий, «Вы» — в ряду BotPeople; человек с фото — только если фото уже в S), фон, голос.
    Фрагмент и кадр — у файлов (их ставит сервер экрана, когда файл пришёл) */
export function applyView(h: DanceViewHooks, view: Partial<DanceView>): void {
  const { S } = h;
  if (view.who) {
    S.auto = true;
    if (view.who === 'char' && view.char) {
      S.cid = view.char;
      S.skin = view.skin ? `${view.char}:${view.skin}` : null;
      S.ppl = [];
      S.phOn = false;
      h.P.setIn(false);
    } else if (view.who === 'p' && view.person) {
      S.cid = null;
      S.phOn = false;
      S.ppl = [{ id: view.person, act: null, own: '' }];
      h.P.setIn(false);
    } else if (view.who === 'me') {
      S.cid = null;
      S.phOn = false;
      S.ppl = [];
      h.P.setIn(true);
    } else if (view.who === 'ph') {
      S.cid = null;
      S.ppl = [];
      S.phOn = !!S.ph;
      h.P.setIn(false);
    }
  }
  if (view.bg) S.bg = view.bg;
  if (view.vm) {
    S.vm = view.vm;
    if (view.vm === 'voice' && view.voice) S.voice = view.voice;
  }
}
